import type { Subscription } from '../types';

/**
 * Check if user has a valid subscription (active or valid trial)
 */
export function hasValidSubscription(subscription: Subscription | null): boolean {
  if (!subscription) return false;

  // Active paid subscription
  if (subscription.status === 'active') return true;

  // Valid trial period
  if (subscription.status === 'trialing' && subscription.trialEnd) {
    return new Date(subscription.trialEnd) > new Date();
  }

  // 3-day grace period for past_due
  if (subscription.status === 'past_due' && subscription.currentPeriodEnd) {
    const grace = new Date(subscription.currentPeriodEnd);
    grace.setDate(grace.getDate() + 3);
    return grace > new Date();
  }

  return false;
}

/**
 * Calculate remaining trial days
 */
export function getTrialDaysRemaining(subscription: Subscription | null): number {
  if (!subscription || subscription.status !== 'trialing' || !subscription.trialEnd) {
    return 0;
  }

  const trialEnd = new Date(subscription.trialEnd);
  const now = new Date();
  const diff = trialEnd.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

/**
 * Get subscription display info
 */
export function getSubscriptionStatus(subscription: Subscription | null): {
  label: string;
  color: 'green' | 'blue' | 'yellow' | 'red' | 'gray';
  isValid: boolean;
} {
  if (!subscription) {
    return { label: 'No Subscription', color: 'gray', isValid: false };
  }

  switch (subscription.status) {
    case 'active':
      return { label: 'Pro', color: 'green', isValid: true };

    case 'trialing': {
      const days = getTrialDaysRemaining(subscription);
      if (days > 0) {
        return { label: `Trial (${days}d)`, color: 'blue', isValid: true };
      }
      return { label: 'Trial Expired', color: 'red', isValid: false };
    }

    case 'past_due': {
      if (subscription.currentPeriodEnd) {
        const grace = new Date(subscription.currentPeriodEnd);
        grace.setDate(grace.getDate() + 3);
        if (grace > new Date()) {
          return { label: 'Past Due', color: 'yellow', isValid: true };
        }
      }
      return { label: 'Past Due', color: 'red', isValid: false };
    }

    case 'canceled':
      return { label: 'Canceled', color: 'red', isValid: false };

    case 'unpaid':
      return { label: 'Unpaid', color: 'red', isValid: false };

    case 'incomplete':
      return { label: 'Incomplete', color: 'yellow', isValid: false };

    default:
      return { label: 'Unknown', color: 'gray', isValid: false };
  }
}

/**
 * Format price for display
 */
export function formatPrice(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(amount);
}
